import { useContext, useEffect, useCallback, useState, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * BrowserRouter 환경에서 네비게이션 차단 훅.
 * useBlocker는 Data Router(createBrowserRouter)에서만 동작하므로,
 * navigator.push/replace를 인터셉트하여 동일한 인터페이스를 제공한다.
 *
 * @param {boolean | ((nav: { kind: 'push'|'replace', to: any, options: any }) => boolean)} shouldBlock
 *   true(또는 이동마다 부르는 판별 함수가 true)이면 페이지 이동을 차단
 * @returns {{ state: 'blocked'|'unblocked', proceed: () => void, reset: () => void }}
 */
export default function useNavBlocker(shouldBlock) {
  const { navigator } = useContext(NavigationContext);
  const [isBlocked, setIsBlocked] = useState(false);
  const pendingNavRef = useRef(null);
  const shouldBlockRef = useRef(shouldBlock);
  shouldBlockRef.current = shouldBlock;

  useEffect(() => {
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const blocks = (kind, args) => {
      const rule = shouldBlockRef.current;
      if (typeof rule !== 'function') return Boolean(rule);
      try {
        return Boolean(rule({ kind, to: args[0], options: args[2] }));
      } catch {
        return false;
      }
    };

    navigator.push = function (...args) {
      if (blocks('push', args)) {
        pendingNavRef.current = () => originalPush.apply(navigator, args);
        setIsBlocked(true);
        return;
      }
      return originalPush.apply(navigator, args);
    };

    navigator.replace = function (...args) {
      if (blocks('replace', args)) {
        pendingNavRef.current = () => originalReplace.apply(navigator, args);
        setIsBlocked(true);
        return;
      }
      return originalReplace.apply(navigator, args);
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator]);

  const proceed = useCallback(() => {
    setIsBlocked(false);
    const pendingFn = pendingNavRef.current;
    pendingNavRef.current = null;
    if (pendingFn) pendingFn();
  }, []);

  const reset = useCallback(() => {
    setIsBlocked(false);
    pendingNavRef.current = null;
  }, []);

  return { state: isBlocked ? 'blocked' : 'unblocked', proceed, reset };
}
